import { useState } from 'react';
import { DefaultErrorBoundary } from '@mews-commander/ui';
import { Button, Table, TableActiveColumnFilters, TableHeader, TableProvider, useTableState } from '@optimus-web/core';
import { EmailWhitelistConfiguration } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { lt, useDialog } from '@mews/framework';
import { useGetWhitelistedUserEmails } from '../actions';
import { useColumns } from './useColumns';
import { WhitelistedUserEmailDialog } from './WhitelistedUserEmailDialog';
import { useWhitelistedUserEmailForm } from './useWhitelistedUserEmailForm';

interface WhitelistedUserEmailsTableProps {
    enterpriseName: string;
    enterpriseId: string;
}

export const WhitelistedUserEmailsTable = ({ enterpriseName, enterpriseId }: WhitelistedUserEmailsTableProps) => {
    const [tableState, setTableState] = useTableState<EmailWhitelistConfiguration>();
    const { isLoading, data } = useGetWhitelistedUserEmails({ enterpriseId, tableState });
    const [{ onClose, active }, openDialog] = useDialog();
    const [editingRow, setEditingRow] = useState<EmailWhitelistConfiguration | null>(null);

    const onRowEdit = (id: string) => {
        const emailWhitelistConfiguration = data?.configurations.find((row) => row.id === id) ?? null;
        setEditingRow(emailWhitelistConfiguration);
        form.reset({ email: emailWhitelistConfiguration?.email, emailTypes: emailWhitelistConfiguration?.emailTypes });
        openDialog();
    };

    const columns = useColumns({ onRowEdit });

    const onDialogClose = () => {
        setEditingRow(null);
        form.reset({ email: '', emailTypes: [] });
        onClose();
    };

    const form = useWhitelistedUserEmailForm({
        selectedRow: editingRow,
        enterpriseId,
        closeDialog: onDialogClose,
        availableUsers: data?.availableUsers ?? [],
    });

    return (
        <DefaultErrorBoundary>
            <TableProvider state={tableState} onStateChange={setTableState} columns={columns}>
                <TableHeader
                    title={enterpriseName}
                    globalSearch
                    rightSide={
                        <Button onClick={openDialog} type="button">
                            {lt(TextKey.WhitelistedUserEmailsTableAddUserButton)}
                        </Button>
                    }
                />
                <TableActiveColumnFilters />
                <Table
                    uniqueRowId="id"
                    data={data?.configurations ?? []}
                    loading={isLoading}
                    amountOfRows={data?.configurations.length ?? 0}
                    emptyState={{
                        primaryText: lt(TextKey.WhitelistedUserEmailsTableNoData),
                    }}
                />
            </TableProvider>
            <WhitelistedUserEmailDialog
                form={form}
                onClose={onDialogClose}
                active={active}
                selectedRow={editingRow}
                enterpriseId={enterpriseId}
                availableUsers={data?.availableUsers ?? []}
                availableEmailTypes={data?.availableEmailTypes ?? []}
            />
        </DefaultErrorBoundary>
    );
};
